import {isApp} from "common/utils";
import styles from "components/modal/appInstallCouponModal.module.scss";
import BottomModal from "components/BottomModal";
import classNames from "classnames";
import utilStyles from "styles/utils.module.scss";
import FullButton from "components/buttons/FullButton";
import {couponsRoute} from "common/const";
import Link from "next/link";
import {useRouter} from "next/router";
import {useContext, useEffect, useMemo} from "react";
import {Axios} from "api";
import {redirectToApp, redirectToLogin} from "common/redirect";
import {captureException, captureMessage} from "@sentry/nextjs";
import {toast} from "react-toastify";
import ModalContext from "context/ModalContext";

export const AppInstallCouponModal = (props) => {

    switch (isApp()) {
        case true:
            return AppInstallCouponModalFromApp(props);
        case false:
            return AppInstallCouponModalFromWeb(props);
    }

    return null;
}


const AppInstallCouponModalFromApp = (props) => {
    const {isOpen, close, modalInfo} = props;
    const router = useRouter();
    const {receivedCouponMoldIdsRef} = useContext(ModalContext);

    const couponRate = modalInfo?.coupon_molds?.map(e => e.amount).reduce((a, b) => a + b, 0) || 0;
    const extraPoint = modalInfo?.coupon_molds?.map(e => e.extra_points).reduce((a, b) => a + b, 0) || 0;

    const APP_INSTALL_FROM_APP_INFOS = useMemo(() => {
        return {
            titles: ['앱으로 오신 걸 환영해요!'],
            descriptions: ['10% 앱 다운로드 쿠폰을 쿠폰함에 넣어드렸어요'],
        }
    }, [couponRate, extraPoint]);

    const onSuccess = (moldIds) => {
        receivedCouponMoldIdsRef.current = receivedCouponMoldIdsRef.current.concat(moldIds);
    };

    const downloadCoupon = async (coupon) => {
        try {
            const data = {
                mold_key: coupon.mold_key,
            }
            const res = await Axios.post('v1/coupons/download', data);
            if (res.status < 400) {
                onSuccess([coupon.id]);
            } else if (res.status === 401) {
                redirectToLogin(router, true);
            } else {
                captureMessage(JSON.stringify(res.data));
                toast.info(res.data.display_message || (
                    coupon?.is_point_only ?
                        "일시적인 오류로 포인트 받기에 실패했습니다. 잠시 후 시도해주세요."
                        :
                        "일시적인 오류로 쿠폰 다운로드에 실패했습니다. 잠시 후 다시 시도해주세요."
                ),
                    {autoClose: 1000}
                );
            }
        } catch (e) {
            captureException(e);
            if (coupon?.is_point_only) {
                toast.info("일시적인 오류로 포인트 받기에 실패했습니다. 잠시 후 시도해주세요.", {autoClose: 1000});
            } else {
                toast.info("일시적인 오류로 쿠폰 다운로드에 실패했습니다. 잠시 후 다시 시도해주세요.", {autoClose: 1000});
            }
        }
    }

    useEffect(() => {
        if (isOpen && modalInfo?.coupon_molds?.length > 0) {
            downloadCoupon(modalInfo?.coupon_molds[0]);
        }
    }, [isOpen])

    const applyHighlightText = (text) => {
        const regex = /{(.*?)}/g;
        const parts = [];
        let lastIndex = 0;
        let match;

        while ((match = regex.exec(text)) !== null) {
            if (match.index > lastIndex) {
                parts.push(text.slice(lastIndex, match.index));
            }
            parts.push(<span className={styles.titleHighlightWrapper} key={match.index}>{match[1]}</span>);
            lastIndex = regex.lastIndex;
        }

        if (lastIndex < text.length) {
            parts.push(text.slice(lastIndex));
        }

        return <span className={styles.titleWrapper}>{parts}</span>;
    }

    const imageLink = 'https://d1cnx04b8cgzcv.cloudfront.net/media/modal/app_download.svg';

    const onCancelClick = () => {
        close();
    }

    const onConfirmClick = () => {
        close();
    }

    return (
        <BottomModal isOpen={isOpen} close={close}>
            <div className={styles.root}>
                <div className={styles.titleWrapper}>
                    {
                        APP_INSTALL_FROM_APP_INFOS?.titles?.map((text, index) => (
                            <div key={'app-install-modal-title-' + index} className={styles.title}>
                                {applyHighlightText(text)}
                            </div>
                        ))
                    }
                </div>
                <div className={styles.descriptionWrapper}>
                    {
                        APP_INSTALL_FROM_APP_INFOS?.descriptions?.map((text, index) => (
                            <div key={'app-install-description-' + index} className={styles.description}>
                                {text}
                            </div>
                        ))
                    }
                </div>
                <object
                    data={imageLink}
                    type="image/svg+xml"
                    width={130}
                    height={164}
                />
                <div
                    className={classNames(utilStyles.flexRow, utilStyles.alignItemsCenter, styles.bottomButtonContainer)}
                >
                    <div className={styles.cancelButtonWrapper}>
                        <FullButton white title={'닫기'} height={48} fontSize={16}
                                    onClick={onCancelClick} />
                    </div>
                    <div className={styles.confirmButtonWrapper}>
                        <Link href={couponsRoute}>
                            <FullButton title={'쿠폰함 확인하기'} height={48} fontSize={16}
                                        onClick={onConfirmClick}/>
                        </Link>
                    </div>
                </div>
            </div>
        </BottomModal>
    )
}


const AppInstallCouponModalFromWeb = (props) => {
    const {isOpen, close, modalInfo} = props;

    const couponRate = modalInfo?.coupon_molds?.map(e => e.amount).reduce((a, b) => a + b, 0) || 0;
    const extraPoint = modalInfo?.coupon_molds?.map(e => e.extra_points).reduce((a, b) => a + b, 0) || 0;

    const APP_INSTALL_FROM_WEB_INFOS = useMemo(() => {
        let text = '';

        if (couponRate > 0) {
            text += `${couponRate}% 쿠폰`;
        }
        if (couponRate > 0 && extraPoint > 0) {
            text += ' + ';
        }
        if (extraPoint) {
            text += `${extraPoint}P`;
        }

        return {
            titles: ['지금 앱 다운받고', '{10% 할인 쿠폰} 받으세요!'],
            descriptions: ['헤메코는 앱에서 다양한 혜택을 제공하고 있어요', '놓치지 말고 받아가세요 :)'],
        };
    }, [couponRate, extraPoint]);

    const applyHighlightText = (text) => {
        const regex = /{(.*?)}/g;
        const parts = [];
        let lastIndex = 0;
        let match;

        while ((match = regex.exec(text)) !== null) {
            if (match.index > lastIndex) {
                parts.push(text.slice(lastIndex, match.index));
            }
            parts.push(<span className={styles.titleHighlightWrapper} key={match.index}>{match[1]}</span>);
            lastIndex = regex.lastIndex;
        }

        if (lastIndex < text.length) {
            parts.push(text.slice(lastIndex));
        }

        return <span className={styles.titleWrapper}>{parts}</span>;
    }

    const imageLink = 'https://d1cnx04b8cgzcv.cloudfront.net/media/modal/hemeko_download_icon.svg';

    const onCancelClick = () => {
        close();
    }

    const onConfirmClick = () => {
        redirectToApp();
        close();
    }

    return (
        <BottomModal isOpen={isOpen} close={close}>
            <div className={styles.root}>
                <div className={styles.titleWrapper}>
                    {
                        APP_INSTALL_FROM_WEB_INFOS?.titles?.map((text, index) => (
                            <div key={'app-install-modal-title-' + index} className={styles.title}>
                                {applyHighlightText(text)}
                            </div>
                        ))
                    }
                </div>
                <div className={styles.descriptionWrapper}>
                    {
                        APP_INSTALL_FROM_WEB_INFOS?.descriptions?.map((text, index) => (
                            <div key={'app-install-description-' + index} className={styles.description}>
                                {text}
                            </div>
                        ))
                    }
                </div>
                <object
                    data={imageLink}
                    type="image/svg+xml"
                    className={styles.svgWrapper}
                />
                <div
                    className={classNames(utilStyles.flexRow, utilStyles.alignItemsCenter, styles.bottomButtonContainer)}
                >
                    <div className={styles.cancelButtonWrapper}>
                        <FullButton white title={'다음에'} height={48} fontSize={16}
                                    onClick={onCancelClick}/>
                    </div>
                    <div className={styles.confirmButtonWrapper}>
                        <FullButton title={'앱 다운받기'} height={48} fontSize={16}
                                    onClick={onConfirmClick}/>
                    </div>
                </div>
            </div>
        </BottomModal>
    )
}